import type { CSSProperties, JSX } from 'react';

/**
 * A burst of confetti.
 *
 * Rendered whenever something rare and good happens — a concept mastered, a
 * lesson finished, a perfect set. Not on every correct answer: a celebration
 * that fires constantly stops being one.
 *
 * The scatter is **derived from the piece index, not from `Math.random()`**.
 * That keeps this package inside the no-randomness rule, and it means two
 * bursts look identical, which is what you want from a thing that is supposed
 * to read as "the app is happy" rather than as noise. Motion is pure CSS, so
 * `prefers-reduced-motion` in `theme.css` switches the whole thing off.
 */

const COLOURS = ['var(--grass-face)', 'var(--sky-face)', 'var(--gold-face)', 'var(--grape-face)', 'var(--berry-face)'];

/**
 * A cheap deterministic spread.
 *
 * Multiplying by an irrational-ish constant and taking the fractional part
 * scatters consecutive indices without ever repeating a pattern the eye can
 * pick out — the same trick a golden-angle phyllotaxis uses.
 */
function spread(index: number, salt: number): number {
  const value = (index + 1) * 0.6180339887 * (salt + 1.7);
  return value - Math.floor(value);
}

export function Confetti({ pieces = 28, className = '' }: { pieces?: number; className?: string }): JSX.Element {
  return (
    <div className={`confetti ${className}`} aria-hidden="true">
      {Array.from({ length: pieces }, (_, index) => {
        const left = spread(index, 0) * 100;
        const drift = (spread(index, 1) - 0.5) * 140;
        const delay = spread(index, 2) * 0.35;
        const duration = 1.1 + spread(index, 3) * 0.7;
        const tilt = spread(index, 4) * 720 - 360;
        const size = 7 + Math.round(spread(index, 5) * 6);

        return (
          <span
            key={index}
            className="confetti__piece"
            style={
              {
                left: `${left}%`,
                width: `${size}px`,
                height: `${Math.round(size * 1.6)}px`,
                background: COLOURS[index % COLOURS.length],
                animationDelay: `${delay}s`,
                animationDuration: `${duration}s`,
                ['--drift' as keyof CSSProperties]: `${drift}px`,
                ['--tilt' as keyof CSSProperties]: `${tilt}deg`,
              } as CSSProperties
            }
          />
        );
      })}
    </div>
  );
}
